import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, X } from "lucide-react";
import { WEEK_DAYS } from "@/util/callFlowNodes";
import { BusinessHoursRange, BusinessHoursSchedule } from "@/model/interface/CallFlow";

interface BusinessHoursEditorProps {
  value: BusinessHoursSchedule;
  timezone?: string;
  title?: string;
  description?: string;
  closedLabel?: string;
  onChange: (schedule: BusinessHoursSchedule) => void;
}

interface BusinessHolidaysEditorProps {
  value: BusinessHoursSchedule;
  onChange: (schedule: BusinessHoursSchedule) => void;
}

const DEFAULT_RANGE = { start: "09:00", end: "17:00" };
const TIME_INPUT_CLASS = "h-8 min-w-0 flex-1 px-1.5 text-sm [&::-webkit-calendar-picker-indicator]:hidden";

function dayLabel(day: string): string {
  return day.charAt(0) + day.slice(1, 3).toLowerCase();
}

export function BusinessHoursEditor({
  value,
  timezone,
  title = "Opening hours *",
  description,
  closedLabel = "Closed",
  onChange,
}: BusinessHoursEditorProps) {
  const days = value.days ?? {};
  const descriptionText =
    description ??
    `Calls inside these hours take the Open branch, everything else takes Closed. Evaluated in your organisation timezone${timezone ? `, ${timezone}` : ""}.`;

  const setDayRanges = (day: string, ranges: BusinessHoursRange[]) => {
    const nextDays = { ...days };
    if (ranges.length === 0) {
      delete nextDays[day];
    } else {
      nextDays[day] = ranges;
    }
    onChange({ ...value, days: nextDays });
  };

  return (
    <div className="space-y-2">
      <div>
        <Label>{title}</Label>
        {descriptionText && <p className="text-xs text-muted-foreground">{descriptionText}</p>}
      </div>

      <div className="divide-y rounded-md border">
        {WEEK_DAYS.map((day) => {
          const ranges = days[day] ?? [];
          const isOpen = ranges.length > 0;

          return (
            <div key={day} className="flex items-start gap-2 px-2 py-1.5">
              <span className="w-8 shrink-0 pt-1.5 text-sm font-medium">{dayLabel(day)}</span>
              <Switch
                className="mt-1.5"
                checked={isOpen}
                onCheckedChange={(checked) => setDayRanges(day, checked ? [{ ...DEFAULT_RANGE }] : [])}
              />
              <div className="min-w-0 flex-1 space-y-1">
                {!isOpen && <span className="block pt-1.5 text-sm text-muted-foreground">{closedLabel}</span>}

                {ranges.map((range, rangeIndex) => (
                  <div key={`${day}-${rangeIndex}`} className="flex items-center gap-1">
                    <Input
                      type="time"
                      className={TIME_INPUT_CLASS}
                      value={range.start}
                      onChange={(changeEvent) => {
                        const nextRanges = [...ranges];
                        nextRanges[rangeIndex] = { ...range, start: changeEvent.target.value };
                        setDayRanges(day, nextRanges);
                      }}
                    />
                    <span className="text-xs text-muted-foreground">to</span>
                    <Input
                      type="time"
                      className={TIME_INPUT_CLASS}
                      value={range.end}
                      onChange={(changeEvent) => {
                        const nextRanges = [...ranges];
                        nextRanges[rangeIndex] = { ...range, end: changeEvent.target.value };
                        setDayRanges(day, nextRanges);
                      }}
                    />
                    {ranges.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        title="Remove these hours"
                        onClick={() => setDayRanges(day, ranges.filter((_, index) => index !== rangeIndex))}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}

                {isOpen && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-1.5 text-xs"
                    onClick={() => setDayRanges(day, [...ranges, { ...DEFAULT_RANGE }])}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add hours
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function BusinessHolidaysEditor({ value, onChange }: BusinessHolidaysEditorProps) {
  const holidays = value.holidays ?? [];

  return (
    <div className="space-y-2">
      <div>
        <Label>Holidays</Label>
        <p className="text-xs text-muted-foreground">These dates are closed all day, whatever the weekly hours say.</p>
      </div>

      {holidays.map((holiday, holidayIndex) => (
        <div key={`holiday-${holidayIndex}`} className="flex items-center gap-2">
          <Input
            type="date"
            className="h-8 min-w-0 flex-1"
            value={holiday}
            onChange={(changeEvent) => {
              const nextHolidays = [...holidays];
              nextHolidays[holidayIndex] = changeEvent.target.value;
              onChange({ ...value, holidays: nextHolidays });
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            title="Remove this holiday"
            onClick={() => onChange({ ...value, holidays: holidays.filter((_, index) => index !== holidayIndex) })}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange({ ...value, holidays: [...holidays, ""] })}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add holiday
      </Button>
    </div>
  );
}
